import type { Annotations, Frame, POI, PresignFrameResponse } from "../api/types";
import type { PutResult } from "../api/annotations";
import type { TrackSample } from "../export/track";
import { CaptureFatalError } from "@viewer-grabber";

export interface LatLon { lat: number; lon: number }
export interface FramePosition extends LatLon { source: "centre" | "aircraft" }

/** What the shared viewer's renderFreezeFrame needs to burn in and tag. */
export interface FreezeFields {
  utcS: number;
  tz: string;
  flightId: string;
  poiName?: string;
  videoTimeS: number;
  position: FramePosition | null;
  aircraft: LatLon | null;
}

/** No capture can work (tainted canvas, video will not load): stop the run.
 *  It lives with the grabber in the shared viewer, whose POI "Save frame"
 *  uses the grabber too; one class, so `instanceof` holds wherever it is
 *  thrown. */
export { CaptureFatalError };

/** Something that can show the flight's video at a time and hand back the picture. */
export interface FrameGrabber {
  /** Video length in seconds. */
  readonly duration: number;
  /** Seek and return the whole picture. Throws CaptureFatalError when no
   *  capture can ever work; any other error fails only this one. */
  grab(videoTimeS: number): Promise<HTMLCanvasElement>;
  dispose(): void;
}

/** The slice of AnnotationsApi the loop uses. */
export interface CaptureApi {
  get(flightId: string, team?: string): Promise<{ annotations: Annotations | null; etag: string | null }>;
  put(flightId: string, body: Annotations, ifMatchEtag: string | null, team?: string): Promise<PutResult>;
  presignFrame(
    flightId: string, frameId: string, kind: "full" | "thumb",
    contentType: string, contentLength: number, team?: string,
  ): Promise<PresignFrameResponse>;
  uploadFrame(presignedUrl: string, blob: Blob, contentType: string): Promise<void>;
}

export interface CapturePoiFramesInput {
  flightId: string;
  team?: string;
  /** Epoch seconds of video time zero (`meta.videoStartUtc`). */
  videoStartUtcS: number;
  tz: string;
  track: TrackSample[];
  /** The picture's centre on the ground at a video time, or null. */
  centreAt: (t: number) => LatLon | null;
  reviewer: { reviewerId: string; displayName: string };
  grabber: FrameGrabber;
  render: (
    canvas: HTMLCanvasElement, fields: FreezeFields,
  ) => Promise<{ fullBlob: Blob; thumbBlob: Blob | null } | null>;
  resolvePosition: (p: { centre: LatLon | null; aircraft: LatLon | null }) => FramePosition | null;
  api: CaptureApi;
  newId?: () => string;
  signal?: AbortSignal;
  onProgress?: (done: number, total: number) => void;
}

export interface CapturePoiFramesResult {
  saved: number;
  failed: number;
  stopped: boolean;
  total: number;
}

/** How close to the end a seek may land; the last instant rarely decodes. */
const END_MARGIN_S = 0.05;

/** POIs no saved frame points at, in video-time order. */
export function poisWithoutFrame(ann: Annotations | null): POI[] {
  if (ann === null) return [];
  const framed = new Set(ann.frames.map((f) => f.poiId));
  return ann.pois
    .filter((p) => Number.isFinite(p.t) && !framed.has(p.id))
    .sort((a, b) => a.t - b.t);
}

/** Aircraft position at video time `t`, linear between samples. */
export function aircraftAt(track: TrackSample[], t: number): LatLon | null {
  const first = track[0];
  const last = track[track.length - 1];
  if (first === undefined || last === undefined) return null;
  if (t <= first.t) return { lat: first.lat, lon: first.lon };
  if (t >= last.t) return { lat: last.lat, lon: last.lon };
  let lo = 0;
  let hi = track.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if ((track[mid] as TrackSample).t <= t) lo = mid; else hi = mid;
  }
  const a = track[lo] as TrackSample;
  const b = track[hi] as TrackSample;
  const k = b.t === a.t ? 0 : (t - a.t) / (b.t - a.t);
  return { lat: a.lat + (b.lat - a.lat) * k, lon: a.lon + (b.lon - a.lon) * k };
}

async function captureOne(input: CapturePoiFramesInput, poi: POI): Promise<Frame> {
  const { grabber, api, flightId, team } = input;
  const videoTimeS = Math.min(Math.max(poi.t, 0), Math.max(0, grabber.duration - END_MARGIN_S));
  const canvas = await grabber.grab(videoTimeS);
  const aircraft = aircraftAt(input.track, poi.t);
  const position = input.resolvePosition({ centre: input.centreAt(poi.t), aircraft });
  const utcS = input.videoStartUtcS + poi.t;
  let rendered: { fullBlob: Blob; thumbBlob: Blob | null } | null;
  try {
    rendered = await input.render(canvas, {
      utcS, tz: input.tz, flightId, poiName: poi.name, videoTimeS, position, aircraft,
    });
  } finally {
    // A 4608x2592 RGBA canvas is ~48 MB; one is made per POI and WebKit
    // enforces a total canvas memory ceiling, so a run of 50+ POIs must
    // release each canvas's backing memory as soon as render is done with
    // it, well before the garbage collector would notice — the uploads
    // below only need the already-encoded blobs, not the canvas.
    canvas.width = 0;
    canvas.height = 0;
  }
  if (rendered === null || rendered.thumbBlob === null) throw new Error("could not encode");
  const { fullBlob, thumbBlob } = rendered;

  const frameId = (input.newId ?? (() => crypto.randomUUID()))();
  const [full, thumb] = await Promise.all([
    api.presignFrame(flightId, frameId, "full", "image/jpeg", fullBlob.size, team),
    api.presignFrame(flightId, frameId, "thumb", "image/jpeg", thumbBlob.size, team),
  ]);
  await Promise.all([
    api.uploadFrame(full.url, fullBlob, "image/jpeg"),
    api.uploadFrame(thumb.url, thumbBlob, "image/jpeg"),
  ]);
  return {
    id: frameId,
    schemaVersion: 1,
    // The POI's own moment, as a hand-pressed "Save frame" stores it.
    videoTimeS: poi.t,
    utc: utcS,
    lat: position?.lat ?? 0,
    lon: position?.lon ?? 0,
    poiId: poi.id,
    capturedAt: new Date().toISOString(),
    capturedBy: { reviewerId: input.reviewer.reviewerId, displayName: input.reviewer.displayName },
    hasThumb: true,
    hasFull: true,
  };
}

interface Doc { annotations: Annotations; etag: string | null }

/** Whether `poiId` still needs a frame: it exists in `annotations` and no
 *  frame there already points at it. Checked before a capture starts, and
 *  again after a conflict refetch inside appendFrame — someone else may
 *  have framed or deleted the POI while we were grabbing and rendering. */
function poiStillNeedsFrame(annotations: Annotations, poiId: string): boolean {
  return (
    annotations.pois.some((p) => p.id === poiId) &&
    !annotations.frames.some((f) => f.poiId === poiId)
  );
}

/** appendFrame's outcome: "ok" wrote the frame; "skip" means a conflict
 *  refetch showed this POI no longer wants one (someone else framed it, or
 *  it was deleted) — the pictures already uploaded for it are orphaned,
 *  which is accepted rather than trying to verify uploads mid-run. Either
 *  way `doc` is the latest known document, for the loop to carry on with. */
type AppendOutcome = { status: "ok" | "skip"; doc: Doc };

/** Append `frame` (captured for `poiId`) with If-Match. On a conflict,
 *  refetch and check whether the fresh document still wants a frame for
 *  this POI before retrying — if not, skip the write instead of clobbering
 *  a frame someone else already saved, or resurrecting a deleted POI.
 *  Retries the write itself once; every refetch feeds that check, so none
 *  goes unused. */
async function appendFrame(
  input: CapturePoiFramesInput, doc: Doc, frame: Frame, poiId: string,
): Promise<AppendOutcome> {
  const { api, flightId, team } = input;
  let base = doc;
  let retried = false;
  for (;;) {
    const next: Annotations = {
      ...base.annotations,
      frames: [...base.annotations.frames.filter((f) => f.id !== frame.id), frame],
      updatedAt: new Date().toISOString(),
    };
    const res = await api.put(flightId, next, base.etag, team);
    if (res.kind === "ok") return { status: "ok", doc: { annotations: next, etag: res.etag } };
    if (res.kind === "error") throw new Error(res.message);

    const fresh = await api.get(flightId, team);
    if (fresh.annotations === null) throw new Error("annotations disappeared");
    base = { annotations: fresh.annotations, etag: fresh.etag };
    if (!poiStillNeedsFrame(base.annotations, poiId)) return { status: "skip", doc: base };
    if (retried) throw new Error("annotations kept changing");
    retried = true;
  }
}

/**
 * Save a frame for every POI that has none: what pressing "Save frame" on
 * each would do. Each frame's record is written as soon as its pictures are
 * uploaded, so stopping or closing the tab leaves saved frames, never
 * pictures without a record. One POI failing is counted and skipped; a
 * CaptureFatalError ends the run.
 */
export async function capturePoiFrames(
  input: CapturePoiFramesInput,
): Promise<CapturePoiFramesResult> {
  const first = await input.api.get(input.flightId, input.team);
  const todo = poisWithoutFrame(first.annotations);
  const result: CapturePoiFramesResult = { saved: 0, failed: 0, stopped: false, total: todo.length };
  if (first.annotations === null) return result;
  let doc: Doc = { annotations: first.annotations, etag: first.etag };

  let done = 0;
  for (const poi of todo) {
    if (input.signal?.aborted === true) {
      result.stopped = true;
      break;
    }
    // A conflict refetch may have shown this POI framed by someone else, or gone.
    if (poiStillNeedsFrame(doc.annotations, poi.id)) {
      try {
        const frame = await captureOne(input, poi);
        const outcome = await appendFrame(input, doc, frame, poi.id);
        doc = outcome.doc;
        // "skip": the fresh document no longer wants a frame for this POI —
        // counted as neither saved nor failed (see appendFrame).
        if (outcome.status === "ok") result.saved += 1;
      } catch (err) {
        if (err instanceof CaptureFatalError) throw err;
        // The page only counts these; the reason is for whoever opens the
        // console after "N POIs couldn't be captured".
        console.warn(`Include all POIs: POI ${poi.id} not captured:`, (err as Error).message);
        result.failed += 1;
      }
    }
    done += 1;
    input.onProgress?.(done, todo.length);
  }
  return result;
}
